import React, { useState, useEffect } from 'react';
import Header from '../../components/Header';
import { collection, query, orderBy, limit, onSnapshot, where, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { getMintEngineUrl } from '../../config';

const DeleteToken = () => {
  const [tokenId, setTokenId] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteResult, setDeleteResult] = useState(null);
  const [logs, setLogs] = useState([]);
  const [currentStatus, setCurrentStatus] = useState("Prêt à supprimer un token");

  // Écouter les logs depuis Firebase - démarrer dès le montage du composant
  useEffect(() => {
    // Calculer le début et la fin de la journée (aujourd'hui)
    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

    const startOfDayTimestamp = Timestamp.fromDate(startOfDay);
    const endOfDayTimestamp = Timestamp.fromDate(endOfDay);

    const logsRef = collection(db, 'mint-logs');
    // Filtrer les logs de la journée et les trier par date décroissante
    const q = query(
      logsRef,
      where('date', '>=', startOfDayTimestamp),
      where('date', '<=', endOfDayTimestamp),
      orderBy('date', 'desc'),
      limit(100)
    );

    console.log('Écoute des logs démarrée pour la journée:', startOfDay.toLocaleString('fr-FR'));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const newLogs = [];
        snapshot.forEach((doc) => {
          newLogs.push({
            id: doc.id,
            ...doc.data(),
          });
        });
        console.log('Logs reçus:', newLogs.length);
        setLogs(newLogs);

        // Mettre à jour le statut avec le dernier log
        if (newLogs.length > 0) {
          const latestLog = newLogs[0];
          if (latestLog.content) {
            setCurrentStatus(latestLog.content);
          }
        }
      },
      (error) => {
        console.error('Erreur lors de l\'écoute des logs:', error);
      }
    );

    return () => unsubscribe();
  }, []);

  // Fonction pour formater le contenu des logs
  const formatLogContent = (content) => {
    if (!content) return '';

    // Si c'est une string, essayer de la parser comme JSON
    if (typeof content === 'string') {
      try {
        const parsed = JSON.parse(content);
        return JSON.stringify(parsed, null, 2);
      } catch (e) {
        // Si ce n'est pas du JSON valide, retourner tel quel
        return content;
      }
    }

    // Si c'est déjà un objet, le formater en JSON indenté
    if (typeof content === 'object') {
      return JSON.stringify(content, null, 2);
    }

    return String(content);
  };

  // Fonction pour vérifier si le contenu est du JSON
  const isJSON = (content) => {
    if (!content) return false;
    if (typeof content === 'object') return true;
    if (typeof content === 'string') {
      try {
        JSON.parse(content);
        return true;
      } catch (e) {
        return false;
      }
    }
    return false;
  };

  const handleDelete = async () => {
    if (!tokenId || tokenId.trim() === "") {
      alert("Veuillez entrer un tokenId");
      return;
    }

    setIsDeleting(true);
    setCurrentStatus("Suppression du token en cours...");
    setDeleteResult(null);

    try {
      const apiUrl = getMintEngineUrl();
      const response = await fetch(`${apiUrl}/delete-token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          tokenId: tokenId.trim(),
        }),
      });

      const resultData = await response.json();

      if (!response.ok) {
        throw new Error(resultData.error || `Erreur HTTP: ${response.status}`);
      }

      console.log("Résultat de la suppression:", resultData);
      setDeleteResult({ success: true, data: resultData });
      setCurrentStatus(`Token ${tokenId} supprimé avec succès. Attente des logs...`);

      // Réinitialiser le champ tokenId après succès
      setTokenId("");
    } catch (error) {
      console.error("Erreur lors de la suppression du token:", error);
      setCurrentStatus(`Erreur: ${error.message}`);
      setDeleteResult({ success: false, error: error.message });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !isDeleting) {
      handleDelete();
    }
  };

  return (
    <div className="min-h-screen bg-[#00263a]">
      <Header />
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <h1 className="text-3xl font-bold text-white mb-6">Supprimer un Token</h1>

        {/* Section de suppression */}
        <div className="bg-gray-800 rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-white mb-4">Suppression de token</h2>
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-white mb-1">
                Token ID *
              </label>
              <input
                type="text"
                value={tokenId}
                onChange={(e) => setTokenId(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="ex: 6452"
                disabled={isDeleting}
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={handleDelete}
                disabled={isDeleting || !tokenId.trim()}
                className={`${isDeleting || !tokenId.trim()
                  ? "bg-gray-600 cursor-not-allowed"
                  : "bg-red-600 hover:bg-red-700"
                  } text-white px-8 py-2 rounded-md font-semibold transition-colors`}
              >
                {isDeleting ? "Suppression..." : "Supprimer"}
              </button>
            </div>
          </div>
        </div>

        {/* Section Informations */}
        <div className="bg-gray-800 rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-white mb-4">Informations</h2>
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <div className={`w-3 h-3 rounded-full ${isDeleting ? 'bg-yellow-500 animate-pulse' :
                deleteResult?.success ? 'bg-green-500' :
                  deleteResult?.success === false ? 'bg-red-500' : 'bg-gray-500'
                }`}></div>
              <p className="text-white">
                {isDeleting
                  ? "Suppression du token en cours..."
                  : deleteResult?.success
                    ? "Token supprimé avec succès"
                    : deleteResult?.success === false
                      ? `Erreur: ${deleteResult.error}`
                      : "En attente..."}
              </p>
            </div>
            <div className="mt-4 p-3 bg-gray-700 rounded-md">
              <p className="text-sm text-gray-300 mb-2">Statut actuel:</p>
              <div className="bg-gray-800 rounded p-3 max-h-48 overflow-y-auto">
                <p className="text-white font-semibold whitespace-pre-wrap break-words">
                  {isJSON(currentStatus) ? formatLogContent(currentStatus) : currentStatus}
                </p>
              </div>
            </div>
            {deleteResult?.success && deleteResult.data && (
              <div className="mt-4 p-3 bg-green-900 rounded-md">
                <p className="text-sm text-green-300 mb-2">Résultat de la suppression:</p>
                <div className="bg-gray-800 rounded p-3 overflow-x-auto">
                  <pre className="text-white text-xs whitespace-pre-wrap break-words font-mono">
                    {JSON.stringify(deleteResult.data, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Section Logs */}
        <div className="bg-gray-800 rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-white mb-4">Logs (journée en cours)</h2>
          <div className="space-y-3 max-h-[600px] overflow-y-auto">
            {logs.length === 0 ? (
              <p className="text-gray-400 italic">Aucun log disponible pour le moment...</p>
            ) : (
              logs.map((log) => {
                const logContent = log.content || log.message || JSON.stringify(log);
                const formattedContent = isJSON(logContent) ? formatLogContent(logContent) : logContent;

                return (
                  <div
                    key={log.id}
                    className="bg-gray-700 rounded-md p-4 text-sm"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-gray-300 text-xs">
                        {log.date?.toDate ?
                          log.date.toDate().toLocaleString('fr-FR') :
                          log.date ? new Date(log.date.seconds * 1000).toLocaleString('fr-FR') :
                            'Date inconnue'}
                      </span>
                      {log.level && (
                        <span className={`px-2 py-1 rounded text-xs ${log.level === 'error' ? 'bg-red-500' :
                          log.level === 'warning' ? 'bg-yellow-500' :
                            log.level === 'success' ? 'bg-green-500' :
                              'bg-blue-500'
                          }`}>
                          {log.level}
                        </span>
                      )}
                    </div>
                    <div className="bg-gray-800 rounded p-3 overflow-x-auto">
                      <pre className="text-white text-xs whitespace-pre-wrap break-words font-mono">
                        {formattedContent}
                      </pre>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteToken;

